/**
 * Vault — reglas PURAS de acceso (sin AWS, testeadas). Viven en shared/ porque las
 * usa el backend (vault-access.ts) y el front (el Vault de demo del workshop).
 *
 * Quién es "principal" de una persona y qué permiso efectivo tiene sobre un
 * secreto. El resolver que consulta DynamoDB y Cognito vive en vault-access.ts;
 * acá no entra nada que no se pueda testear en un `node --test`.
 *
 * Reglas (doc §6.2, copiadas de Passbolt):
 *  - `owner` solo se otorga a una persona (user#). Un grant `owner` a un grupo se
 *    degrada a `edit`.
 *  - Un grant a un grupo ad-hoc queda topeado por el rol del miembro:
 *    manager/editor → hasta `edit`, reader → `read`.
 *  - Un grant a un grupo de área vale hasta `edit` para todos sus miembros.
 *  - El efectivo es el MÁXIMO entre todos los grants que alcanzan a la persona.
 *  - Admin de Internal NO tiene permiso implícito: sin grant, no revela.
 */

import {
  VAULT_GROUP_ROLE_CAP,
  VAULT_PERMISSION_LEVEL,
  adhocGroupPrincipal,
  areaGroupPrincipal,
  parsePrincipal,
  userPrincipal,
  type VaultGroupRole,
  type VaultPermission,
} from './types/vault';

export interface AdhocMembership {
  groupId: string;
  role: VaultGroupRole;
}

export interface PrincipalSet {
  /** Todos los principals de la persona, el user# primero. */
  principals: string[];
  /** groupId → rol, para topear grants a grupos ad-hoc. */
  adhocRoles: Record<string, VaultGroupRole>;
}

/**
 * @param cognitoGroups  los grupos del ID token (ctx.user.groups)
 * @param managedGroups  los que sincroniza cognito-area-sync (MANAGED_GROUPS) — solo esos
 *                       cuentan como "área"; `admin` o `clapper` no son áreas.
 */
export function principalsFor(
  email: string,
  cognitoGroups: readonly string[],
  managedGroups: readonly string[],
  adhoc: readonly AdhocMembership[]
): PrincipalSet {
  const managed = new Set(managedGroups);
  const principals = [userPrincipal(email)];
  for (const g of cognitoGroups) if (managed.has(g)) principals.push(areaGroupPrincipal(g));
  const adhocRoles: Record<string, VaultGroupRole> = {};
  for (const m of adhoc) {
    principals.push(adhocGroupPrincipal(m.groupId));
    adhocRoles[m.groupId] = m.role;
  }
  return { principals, adhocRoles };
}

const minPerm = (a: VaultPermission, b: VaultPermission): VaultPermission =>
  VAULT_PERMISSION_LEVEL[a] <= VAULT_PERMISSION_LEVEL[b] ? a : b;
const maxPerm = (a: VaultPermission | null, b: VaultPermission): VaultPermission =>
  !a || VAULT_PERMISSION_LEVEL[b] > VAULT_PERMISSION_LEVEL[a] ? b : a;

export function effectivePermission(
  grants: ReadonlyArray<{ principal: string; permission: VaultPermission }>,
  who: PrincipalSet
): VaultPermission | null {
  const mine = new Set(who.principals);
  let best: VaultPermission | null = null;
  for (const grant of grants) {
    if (!mine.has(grant.principal)) continue;
    const parsed = parsePrincipal(grant.principal);
    if (!parsed) continue;
    let perm: VaultPermission = grant.permission;
    if (parsed.kind === 'area') perm = minPerm(perm, 'edit');
    if (parsed.kind === 'adhoc') {
      const role = who.adhocRoles[parsed.groupId];
      if (!role) continue;
      perm = minPerm(perm, VAULT_GROUP_ROLE_CAP[role]);
    }
    best = maxPerm(best, perm);
  }
  return best;
}

export const can = {
  read: (p: VaultPermission | null) => p !== null,
  edit: (p: VaultPermission | null) => p === 'edit' || p === 'owner',
  own: (p: VaultPermission | null) => p === 'owner',
};
